import { CalendarDays } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { Avatar } from '@/components/ui/avatar'
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table'
import { fmtDate } from '../../../shared/format'
import type { Project, Stage } from '../../types'
import { useStaffLookup } from '../../ui'
import { ProgressBar } from '../ProjectBits'
import { RowMenu } from '../RowMenu'
import { StageStatusBadge } from '../StatusBadges'

type Props = { project: Project; stages: Stage[]; canWrite: boolean; onEdit: (s: Stage) => void; onDelete: (s: Stage) => void }

/** Kurulum aşamaları: dar alanda kart listesi, genişte tablo (kapsayıcı genişliğine göre). */
export function StageTable({ project, stages, canWrite, onEdit, onDelete }: Props) {
  const { t } = useTranslation()
  const { nameOf } = useStaffLookup()
  const taskById = new Map(project.tasks.map((x) => [x.id, x]))

  const taskLinks = (s: Stage) =>
    s.taskIds.length ? (
      <span className="flex min-w-0 flex-col gap-0.5">
        {s.taskIds.map((id) => {
          const task = taskById.get(id)
          return task ? (
            <Link key={id} to={`/projects/${project.id}/tasks/${id}`} className="truncate text-xs text-primary hover:underline">
              #{task.key} {task.title}
            </Link>
          ) : null
        })}
      </span>
    ) : (
      <span className="text-xs text-muted-foreground">—</span>
    )
  const owner = (s: Stage) =>
    s.ownerId ? (
      <span className="flex min-w-0 items-center gap-2 text-sm">
        <Avatar name={nameOf(s.ownerId)} className="size-6 [&>span]:text-[9px]" />
        <span className="truncate">{nameOf(s.ownerId)}</span>
      </span>
    ) : (
      <span className="text-sm text-muted-foreground">{t('projects.work.unassigned')}</span>
    )
  const menu = (s: Stage) => (canWrite ? <RowMenu label={s.name} onEdit={() => onEdit(s)} onDelete={() => onDelete(s)} /> : null)

  return (
    <div className="@container overflow-hidden rounded-xl border">
      <ul className="divide-y @3xl:hidden">
        {stages.map((s) => (
          <li key={s.id} className="min-w-0 space-y-2.5 p-3">
            <div className="flex items-start gap-2">
              <div className="min-w-0 flex-1">
                <p className="font-medium">{s.name}</p>
                {s.description ? <p className="text-xs text-muted-foreground">{s.description}</p> : null}
              </div>
              <StageStatusBadge status={s.status} />
              {menu(s)}
            </div>
            <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
              {owner(s)}
              <span className="inline-flex items-center gap-1.5 text-xs tabular-nums text-muted-foreground">
                <CalendarDays className="size-3.5" aria-hidden />
                {fmtDate(s.plannedDate)}
              </span>
            </div>
            <ProgressBar value={s.completion} label={t('projects.install.stageCols.completion')} />
            {s.taskIds.length ? (
              <div className="min-w-0 rounded-lg bg-muted/40 p-2">
                <p className="mb-1 text-[11px] text-muted-foreground">{t('projects.install.stageCols.tasks')}</p>
                {taskLinks(s)}
              </div>
            ) : null}
          </li>
        ))}
      </ul>
      <div className="hidden @3xl:block">
        <Table>
          <THead>
            <TR className="hover:bg-transparent [&>th]:whitespace-nowrap">
              <TH>{t('projects.install.stageCols.name')}</TH>
              <TH className="hidden @5xl:table-cell">{t('projects.fields.description')}</TH>
              <TH>{t('projects.install.stageCols.tasks')}</TH>
              <TH>{t('projects.install.columns.owner')}</TH>
              <TH>{t('projects.install.stageCols.planned')}</TH>
              <TH className="text-right">{t('projects.install.stageCols.completion')}</TH>
              <TH>{t('projects.install.columns.status')}</TH>
              {canWrite ? <TH className="w-12" aria-label={t('projects.work.columns.actions')} /> : null}
            </TR>
          </THead>
          <TBody>
            {stages.map((s) => (
              <TR key={s.id}>
                <TD className="font-medium">{s.name}</TD>
                <TD className="hidden max-w-56 text-sm text-muted-foreground @5xl:table-cell">{s.description}</TD>
                <TD className="max-w-48">{taskLinks(s)}</TD>
                <TD className="max-w-40">{owner(s)}</TD>
                <TD className="whitespace-nowrap text-sm tabular-nums">{fmtDate(s.plannedDate)}</TD>
                <TD className="text-right tabular-nums">%{s.completion}</TD>
                <TD>
                  <StageStatusBadge status={s.status} />
                </TD>
                {canWrite ? <TD className="text-right">{menu(s)}</TD> : null}
              </TR>
            ))}
          </TBody>
        </Table>
      </div>
    </div>
  )
}
